import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty } from "@/components/kit";
import { STATUS_META } from "@/lib/constants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Globe } from "lucide-react";

const COLORS = ["#3457d5", "#6366f1", "#7c3aed", "#0ea5e9", "#10b981", "#f59e0b"];

export default function Analytics() {
  const { activeOrgId } = useAuth();
  const [a, setA] = useState(null);

  useEffect(() => { if (activeOrgId) client.get("/analytics").then((r) => setA(r.data)); }, [activeOrgId]);

  const statusData = a ? Object.entries(a.by_status).filter(([, v]) => v > 0).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v })) : [];

  return (
    <div>
      <PageHeader title="Análises" subtitle="Somente contadores internos reais. Métricas externas aparecem após a conexão oficial." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {a && [["Total", a.total], ["Publicados", a.by_status.PUBLISHED], ["Agendados", a.by_status.SCHEDULED], ["Aprovados", a.by_status.APPROVED]].map(([l, v]) => (
          <Card key={l} data-testid={`analytics-kpi-${l}`}><div className="text-xs text-slate-500 uppercase tracking-wide">{l}</div><div className="text-2xl font-extrabold font-display text-slate-900 mt-1">{v}</div></Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-display font-bold mb-4">Conteúdos por status</h3>
          {statusData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusData}><XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} height={50} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{statusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart>
            </ResponsiveContainer>
          ) : <Empty>Sem dados ainda.</Empty>}
        </Card>
        <Card>
          <h3 className="font-display font-bold mb-4">Conteúdos por canal</h3>
          {a?.by_channel?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={a.by_channel}><XAxis dataKey="channel" tick={{ fontSize: 11 }} interval={0} angle={-15} height={50} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" radius={[6, 6, 0, 0]}>{a.by_channel.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart>
            </ResponsiveContainer>
          ) : <Empty>Sem dados ainda.</Empty>}
        </Card>
      </div>

      <Card className="mt-6 bg-amber-50 border-amber-100">
        <p className="text-sm text-amber-800">{a?.note}</p>
      </Card>

      <Card className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold flex items-center gap-2"><Globe size={18} className="text-[#3457d5]" /> Métricas externas das redes</h3>
          {a?.external?.available
            ? <span data-testid="external-badge-connected" className="text-[10px] rounded-full border border-emerald-300/50 bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">CONECTADO</span>
            : <span data-testid="external-badge-pending" className="text-[10px] rounded-full border border-slate-300/40 bg-slate-100 text-slate-500 px-2 py-0.5 font-bold">AGUARDANDO CONEXÃO</span>}
        </div>
        {a?.external?.available ? (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">{a.external.message}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {(a.external.connected_channels || []).map((ch, idx) => (
                <div key={idx} data-testid={`external-channel-${ch.channel}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="text-sm font-bold text-slate-800 mb-2">{ch.channel}{ch.username ? ` · @${ch.username}` : (ch.name ? ` · ${ch.name}` : "")}</div>
                  <div className="grid grid-cols-2 gap-3">
                    {[["followers", "Seguidores"], ["reach", "Alcance (28d)"], ["engagement", "Engajamento (28d)"], ["media_count", "Publicações"]]
                      .filter(([k]) => ch[k] !== undefined && ch[k] !== null)
                      .map(([k, label]) => (
                        <div key={k}><div className="text-xl font-extrabold font-display">{Number(ch[k]).toLocaleString("pt-BR")}</div><div className="text-[11px] text-slate-500 uppercase">{label}</div></div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="external-pending">{a?.external?.message}</p>
        )}
      </Card>
    </div>
  );
}
